import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ROUTES } from '../../common-files/enums/server-routes';

// Client for the backend SSE stream (GET /realtime/events?channel=&token=). The
// browser EventSource cannot send an Authorization header, so the platform token
// goes in the query string (the backend verifies it the same way as the api).
// Screens open a channel, then refetch when a "something changed" nudge arrives.
export interface RealtimeMessage {
  event: string;
  channel: string;
  payload: any;
}

@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private source: EventSource | null = null;
  private subject = new Subject<RealtimeMessage>();

  constructor(private zone: NgZone) {}

  // open (or re-open) the stream for a channel; returns the message stream.
  connect(channel: string): Observable<RealtimeMessage> {
    this.close();
    const token = localStorage.getItem('userToken') || '';
    if (!channel || !token) return this.subject.asObservable();

    const url = `${ROUTES.REALTIME_EVENTS}?channel=${encodeURIComponent(channel)}&token=${encodeURIComponent(token)}`;
    const source = new EventSource(url);
    // EventSource callbacks fire outside Angular's zone — re-enter so bound views update.
    source.onmessage = (e: MessageEvent) => this.emit(e.data);
    // named SSE events (backend uses `event: <name>`) don't hit onmessage
    source.addEventListener('allocation.changed', (e) => this.emit((e as MessageEvent).data));
    source.addEventListener('booking.locked', (e) => this.emit((e as MessageEvent).data));
    this.source = source;
    return this.subject.asObservable();
  }

  private emit(data: string): void {
    let parsed: RealtimeMessage | null = null;
    try {
      parsed = JSON.parse(data);
    } catch {
      parsed = null;
    }
    if (parsed) this.zone.run(() => this.subject.next(parsed!));
  }

  // the channel for a branch's assign/lock activity.
  establishmentChannel(establishmentId: number | string): string {
    return `establishment:${establishmentId}`;
  }

  close(): void {
    if (this.source) {
      this.source.close();
      this.source = null;
    }
  }
}
